/**
 * Cycle Detail Page (v2.3 - Track-Authoritative)
 * 
 * TRACKS ARE THE COMPARABILITY BOUNDARY.
 * 
 * Cycle-level tabs:
 * - Overview: Cycle summary, ownership, strategic context
 * - Tracks: Dashboard of independent prioritization lanes
 * - Catchball: Loop governance across all tracks
 * - Results: Published Priority Lists
 * - Defaults: Workflow preset + cycle session defaults (inherited by tracks)
 * 
 * REMOVED from cycle root (now track-scoped):
 * - Participation work → /tracks/:trackId
 * - Item pool work → /tracks/:trackId
 * - Criteria and scales → /tracks/:trackId
 * - Values work → /tracks/:trackId
 * - Ranking sessions → /tracks/:trackId
 * 
 * Per spec: "Never: Cycle → Criteria / Pool / Sessions"
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Select } from '@/components/ds';
import { useTossDrawerStore } from '@/stores/prioritization/tossDrawerStore';
import {
  Target,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  CheckCircle2,
  Circle,
  AlertCircle,
  Users,
  FileText,
  Package,
  Play,
  ListChecks,
  Flag,
  ExternalLink,
  X,
  RefreshCcw,
  Loader2,
  Layers,
  ClipboardList,
  Settings,
  Crosshair,
  Lock,
  Activity,
  MessageSquare,
  UserCheck,
  Inbox,
  Send,
  Grid2X2,
} from 'lucide-react';
import { useCycleStore } from '@/stores/prioritization/degapProcessStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useGroupsStore } from '@/stores/prioritization/groupsStore';
import { useModuleGate } from '@/lib/moduleGate';
import { useCycleParticipantsStore } from '@/stores/prioritization/cycleParticipantsStore';
import { useCycleProgress } from '@/stores/prioritization/cycleProgressStore';
import { useCycleReadinessStore } from '@/stores/prioritization/cycleReadinessStore';
import { useCycleComparisonSetStore } from '@/stores/prioritization/cycleComparisonSetStore';
import { AskButton } from '@/components/ask/AskSheet';
import { FollowButton } from '@/components/follow/FollowButton';
import { AssignmentsSection } from '@/components/prioritization/commitments/AssignmentsSection';
import { DecisionRightsPanel } from '@/components/prioritization/cycle';
import { CycleSessionDefaultsSection, CycleGovernanceOverrideSection } from '@/components/prioritization/cycle-config';
import WorkflowPresetSummary from '@/components/prioritization/cycle-config/WorkflowPresetSummary';
import { ContextualReadinessPanel } from '@/components/prioritization/ContextualReadinessPanel';
import { DecisionUnitPanel } from '@/components/prioritization/DecisionUnitPanel';
import { useContextsStore } from '@/stores/prioritization/contextsStore';
import { SlideOverPanelStandalone } from '@/components/panels/SlideOverPanel';
import { ModalPanelStandalone } from '@/components/panels/ModalPanel';
import { useCatchballLoopStore } from '@/stores/prioritization/catchballLoopStore';
import { buildCatchballInboxUrl, isLoopPastFirstSend } from '@/lib/catchballInboxUrls';
import { CATCHBALL_LABELS } from '@/lib/catchball/vocabulary';
import { useAttributeGovernanceStore } from '@/stores/prioritization/attributeGovernanceStore';
import { useExecutionLineageStore } from '@/stores/prioritization/egapIterationStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useValueCollectionStore } from '@/stores/prioritization/valueCollectionStore';
import { OSTSolutionPicker } from '@/components/prioritization/gather';
import { BuildProposalFromOST } from '@/components/prioritization/catchball/BuildProposalFromOST';
import { WorkingTierSetupPanel } from '@/components/prioritization/catchball/WorkingTierSetupPanel';
import { useNextStepStore } from '@/stores/prioritization/nextStepStore';
import { useCycleSessions } from '@/stores/prioritization/sessionStore';
import { usePinnedCycleStore } from '@/stores/prioritization/pinnedCycleStore';
import { CycleMyWorkPanel } from '@/components/prioritization/CycleMyWorkPanel';
import { useUserCycleRoleContext } from '@/hooks/prioritization/useUserCycleRoleContext';
import { canSync } from '@/lib/permissionResolver';
import { getWorkingTierAssignableOrgUnits } from '@/lib/orgUnitTree';
import { DecisionAuthorityView } from '@/components/prioritization/catchball/DecisionAuthorityView';
import { CycleStrategicView } from '@/components/prioritization/CycleStrategicView';
import { TrackIndex } from '@/components/prioritization/TrackIndex';
import { CreateTrackModal } from '@/components/prioritization/tracks/CreateTrackModal';
import { TrackOwnerSubControl } from '@/components/prioritization/TrackOwnerSubControl';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { createTrackWithFeedback } from '@/lib/tracks/createTrackFlow';
import { useWorkflowStore } from '@/stores/prioritization/workflowStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import {
  UNSCOPED_CATEGORY,
  UNSCOPED_ALTITUDE,
} from '@/types/prioritization/track';
import { checkPolicyForCycle } from '@/lib/authorization/storeEnforcement';
import { CycleReportView } from '@/components/prioritization/CycleReportView';
import { useCycleReportStore } from '@/stores/prioritization/cycleReportStore';
import type { CycleReport } from '@/stores/prioritization/cycleReportStore';
import { LineageTimeline } from '@/components/prioritization/activity/LineageTimeline';
import { CycleActivityLog } from '@/components/prioritization/activity/CycleActivityLog';
import { EntityCommentsTab } from '@/components/prioritization/activity/EntityCommentsTab';
import { useCycleLineageEvents } from '@/components/prioritization/activity/useCycleLineageEvents';
import { lazy, Suspense } from 'react';
import { useCycleDesignerEnabled, isCycleDesignerComplete } from '@/features/cycle-designer';
import { ReverseActionButton } from '@/components/reversals/ReverseActionButton';
import { CyclePhaseControl } from '@/components/prioritization/CyclePhaseControl';
import { CellAllocationPanel } from '@/components/prioritization/allocation/CellAllocationPanel';
import { reopenCycleDesign } from '@/lib/reversals/reversalsService';
import {
  FOCUS_RING, LABEL_MONO, panel, heading, chip, status, field,
  btnPrimary, btnSecondary,
} from '@/components/mono/primitives';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';
import { useItemLinkScope } from '@/lib/itemLinkScope';

// Lazy-loaded Exchange list (Catchball opens on its own route — DEP-025)
const CatchballDashboard = lazy(() =>
  import('@/pages/prioritization/catchball/CatchballDashboard').then(m => ({ default: m.CatchballDashboard }))
);

// ─── Design system ────────────────────────────────────────────────────────────
// Square corners, JetBrains Mono labels/buttons, CSS custom-property colours.
// Canonical implementation: src/features/cycle-designer/CycleDesignerWizard.tsx.
//
// The tokens below (--bg, --surface, --rule, --text-*, --slate, --mustard, …) are
// defined twice in src/index.css — dark values on `:root`/`.dark`, light values on
// `html:not(.dark)` — so referencing the token IS the light/dark implementation.
// Therefore: never hardcode a hex here, never add a `dark:` colour variant, and
// never put className="dark" on this tree (that pins the navy palette even for
// users who are in light mode).

// ⚠ WHY BORDER AND HEADING COLOURS ARE INLINE `style` AND NOT CLASSES
// Square / Mono vocabulary lives in @/components/mono/primitives — including
// the cascade note explaining why border and heading colour must be inline.
// HEADER_ACTION_W is the one thing local to this page.

/** Header actions share a width so a short label and a long one read as the
    same control, not two different ones. */
const HEADER_ACTION_W = 'min-w-[8.5rem]';

// ─── Provenance & Discussion sub-component ────────────────────────────────────

function CycleProvenanceTab({ cycleId }: { cycleId: string }) {
  const events = useCycleLineageEvents(cycleId)
  return (
    <div className="space-y-8">
      {/* ── Governance activity (real DB records) ── */}
      <div>
        <h3 {...heading('mb-3')}>Cycle Activity</h3>
        <p className="mb-3 text-xs text-[var(--text-dim)]">
          Operational events — pool locks, ranking runs, criteria changes, publishes.
        </p>
        <CycleActivityLog cycleId={cycleId} />
      </div>

      {/* ── Structural lineage (derived from in-memory store) ── */}
      <div>
        <LineageTimeline events={events} title="Cycle Lifecycle" />
      </div>
    </div>
  )
}

function CycleDiscussionTab({
  cycleId,
  workspaceId,
}: {
  cycleId: string
  workspaceId: string
}) {
  return (
    <EntityCommentsTab
      entityType="cycle"
      entityId={cycleId}
      workspaceId={workspaceId}
    />
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CycleDetail() {
  const { cycleId } = useParams<{ cycleId: string }>();
  useItemLinkScope({ cycleId });
  const navigate = useNavigate();
  const userId = useUserStore(state => state.userId);
  
  // Store selectors
  const cycles = useCycleStore(state => state.cycles);
  const setActiveCycle = useCycleStore(state => state.setActiveCycle);
  // NOTE: canAdvancePhase removed - now using cycleProgress as single source of truth
  const addOutcomeDriver = useCycleStore(state => state.addOutcomeDriver);
  const updateCycle = useCycleStore(state => state.updateCycle);

  // Workflow selector (cycle header)
  const workflows = useWorkflowStore(state => state.workflows);
  const fetchWorkflows = useWorkflowStore(state => state.fetchWorkflows);
  const getEffectiveWorkflowForCycle = useWorkflowStore(state => state.getEffectiveWorkflowForCycle);
  const getEffectiveConfigForCycle = useWorkflowStore(state => state.getEffectiveConfigForCycle);
  
  // Cycle focus mode
  const pinnedCycleId = usePinnedCycleStore(s => s.pinnedCycleId);
  const focusModeEnabled = useModuleGate('cycle_focus_mode');
  // Off for everyone until the owner turns it on (module cycle_phase_control).
  const cyclePhaseEnabled = useModuleGate('cycle_phase_control');
  const decisionRightsEnabled = useModuleGate('decision_rights');
  const enterWorkspace = usePinnedCycleStore(s => s.enterWorkspace);
  const exitWorkspace = usePinnedCycleStore(s => s.exitWorkspace);
  const isFocused = pinnedCycleId === cycleId;

  // Resolve cycle early so any effect/callback that references it runs after it's defined (avoids TDZ)
  const cycle = useMemo(() => cycles.find(c => c.id === cycleId), [cycles, cycleId]);
  
  const items = useItemsStore(state => state.items);
  
  // Get the current workspace context from tenant store (primary source)
  // Falls back to cycle's workspace if available
  const currentWorkspaceId = useTenantStore(state => state.currentWorkspaceId);
  const currentTenantId = useTenantStore(state => state.currentTenantId);
  const cycleWorkspaceId = useCycleStore(state => {
    const c = state.cycles.find(c => c.id === cycleId);
    return c?.workspaceId;
  });
  
  // Use current workspace context (preferred) or fall back to cycle's workspace
  const effectiveWorkspaceId = currentWorkspaceId || cycleWorkspaceId;

  // Value collection: Lock All Tracks
  const lockCycle = useValueCollectionStore(state => state.lockCycle);
  const [lockAllTracksbusy, setLockAllTracksBusy] = useState(false);
  const [showLockAllConfirm, setShowLockAllConfirm] = useState(false);

  const handleLockAllTracks = useCallback(async () => {
    if (!cycleId || !effectiveWorkspaceId || !userId) return;
    setShowLockAllConfirm(false);
    setLockAllTracksBusy(true);
    try {
      await lockCycle({ cycleId, actorId: userId, workspaceId: effectiveWorkspaceId });
    } finally {
      setLockAllTracksBusy(false);
    }
  }, [cycleId, effectiveWorkspaceId, userId, lockCycle]);

  // Workflow selector state
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [workflowSuccess, setWorkflowSuccess] = useState<string | null>(null);
  const [canEditCycleWorkflow, setCanEditCycleWorkflow] = useState(true);
  const [effectiveWorkflowName, setEffectiveWorkflowName] = useState<string | null>(null);
  // The workspace's default, whatever this cycle has chosen: the "use the
  // default" entry names it. It used to borrow the cycle's own workflow name,
  // so picking "Cycle Linked" made that entry read "Cycle Linked" too.
  const [workspaceDefaultWorkflowName, setWorkspaceDefaultWorkflowName] = useState<string | null>(null);
  const [catchballEnabled, setCatchballEnabled] = useState(true);
  const [catchballError, setCatchballError] = useState<string | null>(null);
  // Workspace tab inline sub-view — avoids navigating away so the cycle header stays frozen
  const workflowSelectRef = useRef<HTMLSelectElement | null>(null);
  
  // Fetch system users and groups for role assignments
  // Scope to current workspace/tenant to prevent showing users from other tenants
  const { users: systemUsers } = useSystemUsers({
    workspaceId: effectiveWorkspaceId,
    tenantId: !effectiveWorkspaceId ? currentTenantId : undefined,
  });
  const groups = useGroupsStore(state => state.groups);

  // Display name for the cycle owner, used by the decision-rights matrix so a
  // default-owned right reads as a person rather than a UUID.
  const cycleOwnerName = useMemo(() => {
    if (!cycle?.ownerId) return undefined;
    const owner = systemUsers.find(u => u.id === cycle.ownerId);
    return owner?.name ?? owner?.email ?? undefined;
  }, [cycle?.ownerId, systemUsers]);

  // Catchball Loop store
  const createLoop = useCatchballLoopStore(state => state.createLoop);
  const fetchCatchballLoops = useCatchballLoopStore(state => state.fetchFromSupabase);
  const getLoopsForCycle = useCatchballLoopStore(state => state.getLoopsForCycle);
  const getCurrentLoop = useCatchballLoopStore(state => state.getCurrentLoop);
  
  // Track store (v2.3 architecture)
  const tracks = useTrackStore(state => state.tracks);
  const tracksIsLoading = useTrackStore(state => state.isLoading);
  const getDefaultTrack = useTrackStore(state => state.getDefaultTrack);
  const fetchTracksForCycle = useTrackStore(state => state.fetchTracksForCycle);
  const defaultTrackForCycle = cycleId ? getDefaultTrack(cycleId) : undefined;
  const cycleTracks = useMemo(
    () => (cycleId ? tracks.filter(t => t.cycleId === cycleId) : []),
    [tracks, cycleId]
  );

  // Org hierarchy for auto-created first track (org-level scope)
  const orgUnitsMap = useEnterpriseIdentityStore(state => state.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore(state => state.fetchFromSupabase);
  const userNodeIndex = useEnterpriseIdentityStore(state => state.userNodeIndex);

  // Derived: org units assignable at Catchball working tier (org root + direct divisions)
  const workingTierNodes = useMemo(() => {
    if (!cycle?.orgScopeNodeId) return [];
    return getWorkingTierAssignableOrgUnits(
      cycle.orgScopeNodeId,
      Array.from(orgUnitsMap.values()),
    );
  }, [orgUnitsMap, cycle?.orgScopeNodeId]);

  // Check if any working tier node has at least one user assigned
  const workingTierNodeIds = useMemo(() => new Set(workingTierNodes.map(n => n.id)), [workingTierNodes]);
  const computedWorkingTierHasMembers = useMemo(() => {
    for (const orgUnitId of userNodeIndex.values()) {
      if (workingTierNodeIds.has(orgUnitId)) return true;
    }
    return false;
  }, [userNodeIndex, workingTierNodeIds]);
  const autoCreateFirstTrackInProgress = useRef(false);
  
  // Active loop for this cycle (for proposal packets)
  const activeLoop = useMemo(() => cycleId ? getCurrentLoop(cycleId) : undefined, [cycleId, getCurrentLoop]);
  
  // Next Step store (for Catchball CTAs)
  const fetchNextStepsForCycle = useNextStepStore(state => state.fetchNextStepsForCycle);

  // Sessions for this cycle (tasks must reference a session per canonical model)
  const cycleSessions = useCycleSessions(cycleId ?? '');

  // Role-contextual view — resolves the current user's active job(s) in this cycle.
  // Admin bypass: users with CYCLE_CONFIGURE capability (workspace admins) always
  // see the full admin surface; this hook returns isAdmin=true for them.
  const cycleRoleContext = useUserCycleRoleContext({
    cycleId,
    activeLoopId: activeLoop?.id,
  });
  // Separate check for the "see everything" bypass — workspace admins or explicit permission
  // 'process.edit' maps to CYCLE_CONFIGURE capability — covers workspace admins
  const canSeeFullView =
    cycleRoleContext.isAdmin ||
    cycleRoleContext.isCycleOwner ||
    (effectiveWorkspaceId
      ? canSync('process.edit', { kind: 'resource', id: effectiveWorkspaceId })
      : false);

  // Cycle Participants store (normalized table)
  const fetchParticipants = useCycleParticipantsStore(state => state.fetchParticipantsForCycle);
  
  // Criteria selection store
  const fetchComparisonSet = useCycleComparisonSetStore(state => state.fetchForCycle);
  const allComparisonSets = useCycleComparisonSetStore(state => state.sets);

  // Memoize to avoid infinite loop
  const comparisonSet = useMemo(() =>
    allComparisonSets.find(s => s.cycleId === cycleId),
    [allComparisonSets, cycleId]
  );

  // Attribute Governance store (for value provider configuration)
  const fetchCycleConfigs = useAttributeGovernanceStore(state => state.fetchCycleConfigs);
  const allCycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs);

  // Memoize cycle configs for this cycle
  const cycleConfigs = useMemo(() =>
    allCycleConfigs.filter(c => c.cycleId === cycleId),
    [allCycleConfigs, cycleId]
  );

  // Execution lineage store
  const fetchExecutionLineage = useExecutionLineageStore(state => state.fetchFromSupabase);

  // Cycle Progress (authoritative read-model)
  const { progress: rawCycleProgress, refresh: refreshCycleProgress } = useCycleProgress(cycleId);
  
  // Compute scale readiness client-side (NOT in cycle_progress DB VIEW)
  // This uses cycleConfigs from attributeGovernanceStore + selected criteria from comparisonSet
  const computedScaleReadiness = useMemo(() => {
    const selectedCriteriaKeys = comparisonSet?.attributeIds || [];
    const criteriaCount = selectedCriteriaKeys.length;
    
    if (criteriaCount === 0) {
      return {
        criteriaScalesAcceptedCount: 0,
        criteriaScalesPendingCount: 0,
        criteriaMissingScales: [] as string[],
        criteriaScalesReady: false,
        gateGScalesReady: false,
      };
    }
    
    // Count configs with accepted scales for selected criteria
    const configsForCycle = cycleConfigs.filter(c => selectedCriteriaKeys.includes(c.attributeKey));
    const acceptedCount = configsForCycle.filter(c => c.rangeStatus === 'accepted').length;
    const pendingCount = criteriaCount - acceptedCount;
    const missingScales = selectedCriteriaKeys.filter(key => {
      const config = configsForCycle.find(c => c.attributeKey === key);
      return !config || config.rangeStatus !== 'accepted';
    });
    
    return {
      criteriaScalesAcceptedCount: acceptedCount,
      criteriaScalesPendingCount: pendingCount,
      criteriaMissingScales: missingScales,
      criteriaScalesReady: acceptedCount === criteriaCount,
      gateGScalesReady: acceptedCount === criteriaCount && criteriaCount > 0,
    };
  }, [comparisonSet?.attributeIds, cycleConfigs]);
  
  // Merge rawCycleProgress with computed scale readiness
  const cycleProgress = useMemo(() => {
    if (!rawCycleProgress) return rawCycleProgress;
    return {
      ...rawCycleProgress,
      ...computedScaleReadiness,
    };
  }, [rawCycleProgress, computedScaleReadiness]);
  
  // Cycle Readiness (evaluate_cycle_readiness RPC)
  const evaluateReadiness = useCycleReadinessStore(state => state.evaluateReadiness);

  // Refresh both progress (cycle_progress VIEW) and readiness (evaluate_cycle_readiness).
  // Also refetch cycleConfigs so computed scale readiness updates.
  // Call this whenever the user completes something that could change pre-work / phase gates
  // (e.g. criteria locked, scale defined, values provided, pool locked, participant added).
  const refreshProgressAndReadiness = useCallback(() => {
    if (cycleId && cycle) {
      refreshCycleProgress();
      evaluateReadiness(cycleId);
      fetchCycleConfigs(cycleId);
    }
  }, [cycleId, cycle, refreshCycleProgress, evaluateReadiness, fetchCycleConfigs]);
  
  // Context ID for the Decision Unit panel
  const fetchContextForCycle = useContextsStore(s => s.fetchContextForCycle);
  const [cycleContextId, setCycleContextId] = useState<string | null>(null);
  useEffect(() => {
    if (!cycleId) return;
    void fetchContextForCycle(cycleId).then((ctx) => {
      setCycleContextId(ctx?.id ?? null);
    });
  }, [cycleId, fetchContextForCycle]);

  // Tab-based navigation (v2.3 - track-authoritative, conditions-based)
  // Tracks tab always shown so users can open tracks from cycle detail; Catchball when workflow has it
  type CycleTab = 'overview' | 'tracks' | 'results' | 'allocation' | 'defaults' | 'history' | 'discussion' | 'exchange';
  const [searchParams, setSearchParams] = useSearchParams();
  const designerEnabled = useCycleDesignerEnabled();
  const VALID_TABS: CycleTab[] = ['overview', 'tracks', 'results', 'allocation', 'defaults', 'history', 'discussion', 'exchange'];
  const tabFromUrl = searchParams.get('tab') as CycleTab | null;
  // Derive currentTab from URL so back/forward and shared links always work.
  // 'workspace' is a legacy alias for 'tracks'.
  const currentTab: CycleTab = (tabFromUrl === 'workspace')
    ? 'tracks'
    : (tabFromUrl && VALID_TABS.includes(tabFromUrl)) ? tabFromUrl : 'overview';
  function setCurrentTab(tab: CycleTab) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }
  // Normalise ?tab=workspace in the URL itself so the address bar matches the active tab.
  useEffect(() => {
    if (searchParams.get('tab') === 'workspace') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'tracks');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Open by default: the sessions in a cycle are the work itself, so hiding
  // them behind a click made the tab look emptier than the cycle actually is.
  const [sessionsExpanded, setSessionsExpanded] = useState(true);
  const [configExpanded, setConfigExpanded] = useState(true);
  const [daPickerOpen, setDaPickerOpen] = useState(false);
  const [daSaving, setDaSaving] = useState(false);
  const [daError, setDaError] = useState<string | null>(null);
  /** Expand working-tier assignments under Getting started (Catchball + org scope). */
  const [showWorkingTierPanel, setShowWorkingTierPanel] = useState(false);
  const [workingTierHasMembers, setWorkingTierHasMembers] = useState(false);
  const [checklistCreateTrackOpen, setChecklistCreateTrackOpen] = useState(false);
  const [checklistSendRequestBusy, setChecklistSendRequestBusy] = useState(false);
  useEffect(() => {
  }, [catchballEnabled, currentTab]);
  
  // Local state
  const [showImportModal, setShowImportModal] = useState(false);
  const [savedDrivers, setSavedDrivers] = useState<{ projectName: string; drivers: Array<{ title: string }> } | null>(null);
  const [showOSTPicker, setShowOSTPicker] = useState(false);
  const [showRicePanel, setShowRicePanel] = useState(false);
  const [showWsjfPanel, setShowWsjfPanel] = useState(false);
  const [showPairwiseModal, setShowPairwiseModal] = useState(false);
  const [showProposalBuilder, setShowProposalBuilder] = useState(false);

  // Cycle report for the "Cycle report" card
  const fetchCurrentCycleReport = useCycleReportStore(s => s.fetchCurrentCycleReport);
  const [cycleReport, setCycleReport] = useState<CycleReport | null>(null);

  // Ephemeral handoff only (Build Contract §2.2): outcome drivers from generator/other flow.
  // Not used for persistence; cleared as soon as user imports. Persisted to DB via addOutcomeDriver.
  useEffect(() => {
    if (!cycleId) return;
    
    try {
      const saved = localStorage.getItem(`peak-outcome-drivers-${cycleId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only show if it's for this cycle
        if (parsed.cycleId === cycleId) {
          setSavedDrivers(parsed);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, [cycleId, showImportModal]);
  
  // Get location for navigation state (drivers passed back from generator)
  const location = useLocation();
  const workflowFixRequested = useMemo(
    () => new URLSearchParams(location.search).get('fix') === 'workflow',
    [location.search]
  );
  const hasAutoImported = useRef(false);
  
  // Auto-import drivers passed back from the generator via navigation state
  useEffect(() => {
    if (hasAutoImported.current) return;
    
    const state = location.state as { 
      returnedDrivers?: Array<{ title: string; narrative?: string }>;
      fromGenerator?: boolean;
      forCycleId?: string;
    } | null;
    
    // Only import if the drivers are for this specific cycle
    if (state?.returnedDrivers && state.returnedDrivers.length > 0 && cycle && 
        (!state.forCycleId || state.forCycleId === cycle.id)) {
      hasAutoImported.current = true;
      
      // Import each driver
      state.returnedDrivers.forEach(driver => {
        addOutcomeDriver(cycle.id, driver.title);
      });
      
      // Clear cycle-specific localStorage since we've imported
      localStorage.removeItem(`peak-outcome-drivers-${cycle.id}`);
      setSavedDrivers(null);
      
      // Clear the navigation state to prevent re-import on refresh
      navigate(location.pathname, { replace: true, state: {} });
      
      console.log(`[CycleDetail] Auto-imported ${state.returnedDrivers.length} drivers for cycle ${cycle.id}`);
    }
  }, [location.state, cycle, addOutcomeDriver, navigate, location.pathname]);
  
  // Set active cycle on mount
  useEffect(() => {
    if (cycleId) {
      setActiveCycle(cycleId);
    }
  }, [cycleId, setActiveCycle]);
  
  // Fallback: fetch cycles from Supabase if the cycle isn't in the local store
  // (page refresh / deep-link). This must NOT loop.
  //
  // Root cause of the "never renders, flips back and forth" bug: on a cold load
  // the cycle store frequently fetches BEFORE tenant/workspace context resolves,
  // so the STRICT workspace filter runs with an unresolved/empty scope and returns
  // 0 rows. Because fetchFromSupabase toggles syncState.isLoading, a naive
  // "!cycle && !isLoading" effect re-fires on every load→idle transition and
  // hammers the DB forever, and the render flickers between the spinner and the
  // "not found" state.
  //
  // Fix: (1) wait until workspace resolution is complete, then (2) attempt the
  // fallback at most once per (cycleId, workspaceId) — re-attempting only if the
  // resolved workspace itself changes. The "Cycle not found" state below is gated
  // on this fetch having actually completed, so the race shows a spinner, never a
  // false negative.
  const fetchFromSupabase = useCycleStore(state => state.fetchFromSupabase);
  const syncState = useCycleStore(state => state.syncState);
  const workspaceResolutionComplete = useTenantStore(state => state.workspaceResolutionComplete);
  const cycleFetchAttemptedRef = useRef<string | null>(null);
  const [fallbackFetchSettled, setFallbackFetchSettled] = useState(false);
  useEffect(() => {
    if (!cycleId || cycle || syncState.isLoading) return;
    // Don't fetch or conclude "not found" until we know which workspace we're in.
    if (!workspaceResolutionComplete) return;
    const attemptKey = `${cycleId}::${currentWorkspaceId ?? 'none'}`;
    if (cycleFetchAttemptedRef.current === attemptKey) return;
    cycleFetchAttemptedRef.current = attemptKey;
    setFallbackFetchSettled(false);
    console.log('[CycleDetail] Cycle not found locally, fetching from Supabase...');
    void Promise.resolve(fetchFromSupabase()).finally(() => setFallbackFetchSettled(true));
  }, [
    cycleId,
    cycle,
    syncState.isLoading,
    workspaceResolutionComplete,
    currentWorkspaceId,
    fetchFromSupabase,
  ]);
  
  // Fetch participants from normalized table on mount
  useEffect(() => {
    if (cycleId) {
      fetchParticipants(cycleId);
    }
  }, [cycleId, fetchParticipants]);
  
  // Fetch next steps for CTA display
  useEffect(() => {
    if (cycleId) {
      fetchNextStepsForCycle(cycleId);
    }
  }, [cycleId, fetchNextStepsForCycle]);
  
  // Fetch criteria selection on mount
  useEffect(() => {
    if (cycleId) {
      fetchComparisonSet(cycleId);
    }
  }, [cycleId, fetchComparisonSet]);
  
  // Fetch cycle attribute configs (for value providers) on mount
  useEffect(() => {
    if (cycleId) {
      fetchCycleConfigs(cycleId);
    }
  }, [cycleId, fetchCycleConfigs]);
  
  // Fetch execution-lineage records on mount
  useEffect(() => {
    if (cycleId) {
      fetchExecutionLineage(cycleId);
    }
  }, [cycleId, fetchExecutionLineage]);

  // Fetch tracks for this cycle so single-track redirect and card view have data
  useEffect(() => {
    if (cycleId) {
      fetchTracksForCycle(cycleId);
    }
  }, [cycleId, fetchTracksForCycle]);

  // Fetch cycle report for the "Cycle report" card on Overview / Results tabs
  useEffect(() => {
    if (cycleId) {
      void fetchCurrentCycleReport(cycleId).then(r => setCycleReport(r));
    }
  }, [cycleId, fetchCurrentCycleReport]);

  // Workflow selector: permission and options
  useEffect(() => {
    if (!cycleId || !cycle) return;
    let cancelled = false;
    checkPolicyForCycle('process.edit', cycleId, cycle as unknown as Record<string, unknown>).then((d) => {
      if (!cancelled) setCanEditCycleWorkflow(d.allowed);
    });
    return () => { cancelled = true; };
  }, [cycleId, cycle]);
  useEffect(() => {
    if (!cycle) return;
    let cancelled = false;
    getEffectiveWorkflowForCycle(cycle).then((w) => {
      if (!cancelled) setEffectiveWorkflowName(w?.name ?? null);
    });
    getEffectiveWorkflowForCycle({ ...cycle, workflowId: null }).then((w) => {
      if (!cancelled) setWorkspaceDefaultWorkflowName(w?.name ?? null);
    });
    return () => { cancelled = true; };
  // Pre-existing dependency-array warning. Adding the dep here is behavioural,
  // not cosmetic — this file had a cold-load fetch loop fixed in #499 and the
  // deps are deliberately narrow. Silenced only because a restyle pulls the file
  // into CI's changed-file lint at --max-warnings=0; worth a separate targeted fix.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle?.id, cycle?.workflowId, cycle?.workspaceId, getEffectiveWorkflowForCycle]);
  useEffect(() => {
    if (!cycle) return;
    let cancelled = false;
    getEffectiveConfigForCycle(cycle).then((config) => {
      if (!cancelled) setCatchballEnabled(config.catchballEnabled);
    });
    return () => { cancelled = true; };
  // Pre-existing dependency-array warning. Adding the dep here is behavioural,
  // not cosmetic — this file had a cold-load fetch loop fixed in #499 and the
  // deps are deliberately narrow. Silenced only because a restyle pulls the file
  // into CI's changed-file lint at --max-warnings=0; worth a separate targeted fix.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle?.id, cycle?.workflowId, cycle?.workspaceId, getEffectiveConfigForCycle]);
  useEffect(() => {
    if (cycleId) fetchWorkflows();
  }, [cycleId, fetchWorkflows]);
  useEffect(() => {
    if (!workflowFixRequested || !cycleId) return;

    setCurrentTab('overview');
    setConfigExpanded(true);

    window.requestAnimationFrame(() => {
      workflowSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      workflowSelectRef.current?.focus();
    });

    navigate(location.pathname, { replace: true, state: location.state });
  // Pre-existing dependency-array warning. Adding the dep here is behavioural,
  // not cosmetic — this file had a cold-load fetch loop fixed in #499 and the
  // deps are deliberately narrow. Silenced only because a restyle pulls the file
  // into CI's changed-file lint at --max-warnings=0; worth a separate targeted fix.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowFixRequested, cycleId, navigate, location.pathname, location.state]);
  // A cycle template is applied once, by the server, when the cycle is made
  // (migration 1040). Opening this page never applies it again.

  // Auto-create first track only when cycle has no tracks (avoid duplicate key
  // tracks_unique_default_per_cycle). Skip while Cycle Designer owns setup —
  // designer creates real tracks at launch; a bootstrap lane here becomes an extra.
  useEffect(() => {
    if (!cycleId || tracksIsLoading || autoCreateFirstTrackInProgress.current) return;
    // Wait for cycle hydrate when designer is on; never bootstrap before launch.
    if (designerEnabled && (!cycle || !isCycleDesignerComplete(cycle))) return;
    (async () => {
      await fetchTracksForCycle(cycleId);
      const tracksAfter = useTrackStore.getState().tracks.filter(t => t.cycleId === cycleId);
      if (tracksAfter.length > 0) return;
      if (autoCreateFirstTrackInProgress.current) return;
      autoCreateFirstTrackInProgress.current = true;
      try {
        let root: { id: string; displayName: string } | null = null;
        let units = Array.from(orgUnitsMap.values());
        if (units.length === 0) {
          await fetchOrgUnits();
          const mapAfter = useEnterpriseIdentityStore.getState().orgUnits;
          units = Array.from(mapAfter.values());
        }
        if (cycle?.orgScopeNodeId) {
          root = units.find(ou => ou.id === cycle.orgScopeNodeId) ?? null;
        }
        if (!root) {
          root = units.find(ou => ou.depth === 0 || !ou.parentId) ?? units[0] ?? null;
        }
        const scopeType = root ? 'org_unit' as const : 'default';
        const scopeEntityId = root?.id ?? null;
        const scopeLabel = root?.displayName ?? 'Default';
        const cycleName = cycle?.name ?? 'Track 1';
        await createTrackWithFeedback({
          cycleId,
          name: cycleName,
          description: 'Default comparability lane for this cycle',
          scopeType,
          scopeEntityId,
          scopeLabel,
          requiredItemCategory: UNSCOPED_CATEGORY,
          requiredAltitudeLevel: UNSCOPED_ALTITUDE,
        });
      } finally {
        autoCreateFirstTrackInProgress.current = false;
      }
    })();
  }, [cycleId, tracksIsLoading, orgUnitsMap, fetchOrgUnits, fetchTracksForCycle, cycle, designerEnabled]);

  // Early return AFTER all hooks have been called
  // Prereq A: Cycle must exist and be visible in current workspace before we call readiness or render detail.
  if (!cycleId) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Link
          to="/prioritization/cycles"
          {...btnSecondary('no-underline')}
        >
          View cycles
        </Link>
      </div>
    );
  }
  if (!cycle) {
    // Show the loader while scope is still resolving or the one-shot fallback
    // fetch hasn't completed yet. Only after workspace resolution AND a settled
    // fallback fetch do we treat a missing cycle as genuinely "not found" — this
    // prevents the cold-load race from flashing a false "not found".
    if (syncState.isLoading || !workspaceResolutionComplete || !fallbackFetchSettled) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-8">
          <Loader2 className="h-10 w-10 animate-spin text-[var(--text-dim)]" />
          <p className={LABEL_MONO}>Loading cycle…</p>
        </div>
      );
    }
    return (
      <div className="max-w-md mx-auto text-center py-12 px-4">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-[var(--coral)]" />
        <h2 className="text-lg font-medium text-[var(--text-pri)]">Cycle not found</h2>
        <p className="mt-2 text-sm text-[var(--text-sec)]">
          This link doesn&apos;t resolve in the current workspace.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/prioritization/cycles" {...btnPrimary('no-underline')}>
            View cycles
          </Link>
        </div>
      </div>
    );
  }
  
  const handleWorkflowChange = async (value: string) => {
    if (!cycleId || !cycle) return;
    setWorkflowError(null);
    setWorkflowSuccess(null);
    const workflowId = value === '' ? null : value;
    const result = await updateCycle(cycleId, { workflowId });
    if (result.success) {
      setWorkflowSuccess('Workflow updated. Its presets now flow to this cycle, downstream Tracks, and new sessions unless overridden.');
      window.setTimeout(() => setWorkflowSuccess(null), 4000);
    } else {
      setWorkflowError(result.error ?? 'Failed to update workflow');
    }
  };
  const workflowDisplayName =
    cycle?.workflowId == null
      ? (effectiveWorkflowName ?? 'No workflow configured')
      : workflows.find((w) => w.id === cycle?.workflowId)?.name ?? effectiveWorkflowName ?? cycle?.workflowId ?? '—';
  const workflowDefaultOptionLabel = workspaceDefaultWorkflowName
    ? `Workspace default (${workspaceDefaultWorkflowName})`
    : 'Workspace default';

  // Catchball Loop handlers
  const handleStartLoop = async () => {
    setCatchballError(null);
    if (!cycleId || !userId) {
      setCatchballError('Unable to start: user session or cycle not loaded. Please refresh and try again.');
      return;
    }

    // v2.3: Loops require a track_id. Get or create the default track.
    let defaultTrack = getDefaultTrack(cycleId);

    if (!defaultTrack) {
      // Create the default track if it doesn't exist
      const created = await createTrackWithFeedback({
        cycleId,
        name: cycle?.name ?? 'Track 1',
        description: 'Default Track for this cycle',
        scopeType: 'default',
        scopeLabel: 'Default',
      });

      if (!created.ok || !created.track) {
        setCatchballError(created.error || 'Could not create the default Track. You may need permission to create Tracks.');
        return;
      }
      defaultTrack = created.track;
    }

    const createdLoop = await createLoop(cycleId, defaultTrack.id, userId);
    if (createdLoop) {
      useTossDrawerStore.getState().openToss(cycleId, defaultTrack.id);
    } else {
      const loopErr = useCatchballLoopStore.getState().error;
      setCatchballError(
        loopErr ||
          'Could not start Catchball loop. You may need the Edit Cycles permission, or the catchball_create_loop RPC may not be available.'
      );
    }
  };

  /** Checklist “Send Request →”: create a drafting loop if needed, then open composer (no extra “Start loop” step). */
  const handleChecklistSendFirstRequest = async () => {
    if (!cycleId || !userId) {
      setCatchballError('Unable to start: user session or cycle not loaded. Please refresh and try again.');
      return;
    }
    setChecklistSendRequestBusy(true);
    setCatchballError(null);
    try {
      await fetchCatchballLoops(cycleId);
      const loops = getLoopsForCycle(cycleId);
      const drafting = [...loops]
        .sort((a, b) => b.loopNumber - a.loopNumber)
        .find((l) => l.state === 'drafting');
      if (drafting) {
        useTossDrawerStore.getState().openToss(cycleId, drafting.trackId ?? undefined);
        return;
      }
      await handleStartLoop();
    } finally {
      setChecklistSendRequestBusy(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Compact Header — wraps on a phone: title row first, the stats and
          actions below it */}
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-b border-[var(--rule)] pb-4">
        <div className="flex min-w-0 flex-[1_1_16rem] items-center gap-5">
          <Link
            to="/prioritization/cycles"
            aria-label="Back to cycles"
            className={`inline-flex h-9 w-9 items-center justify-center border text-[var(--text-ter)] transition-colors hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] ${FOCUS_RING}`}
            style={{ borderColor: 'var(--rule-bright)' }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-medium text-[var(--text-pri)]">{cycle.name}</h1>
            {cycle.description && (
              <p className="mt-1 max-w-[62ch] text-sm text-[var(--text-sec)]">{cycle.description}</p>
            )}
            <StructureVersionFinePrint table="degap_cycles" id={cycle.id} mode="started" className="mt-1" />
          </div>
        </div>
        
        {/* Inline stats + focus toggle */}
        {cycleProgress && (
          <div className="flex flex-wrap items-center gap-3 md:gap-7">
            {/* W-1: a designed draft is "ready" (first request pending), not "draft".
                Once a loop exists the exchange has begun — show active, not ready. */}
            {(() => {
              const kickedOff = cycleId ? getLoopsForCycle(cycleId).length > 0 : false;
              const displayStatus =
                cycle.status === 'draft' && isCycleDesignerComplete(cycle)
                  ? (kickedOff ? 'active' : 'ready')
                  : cycle.status;
              return (
                <span {...status(
                  displayStatus === 'active' ? 'green'
                    : displayStatus === 'paused' ? 'mustard'
                    : displayStatus === 'ready' ? 'slate'
                    : 'neutral',
                  'h-9',
                )}>
                  {displayStatus}
                </span>
              );
            })()}
            {cycleProgress.canEnterPrioritize ? (
              <span {...status('green', 'h-9')}>
                <CheckCircle2 className="h-4 w-4" />
                Ranking Ready
              </span>
            ) : (() => {
              const rankingBlockers: string[] = [];
              if (!cycleProgress.gateACriteriaLocked) rankingBlockers.push('Criteria not locked');
              if (!cycleProgress.gateAPoolSelected)   rankingBlockers.push('No item pool selected');
              if (!cycleProgress.gateAPoolLocked)     rankingBlockers.push('Item pool not locked');
              if (rankingBlockers.length === 0) return null;
              return (
                <span
                  {...status('mustard', 'h-9 cursor-default')}
                  title={rankingBlockers.join('\n')}
                >
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Ranking Blocked
                </span>
              );
            })()}

            {/* Ask someone for an estimate or for something done (PRD-tasks-asks-and-my-work FR-1) */}
            <AskButton
              home={{ type: 'cycle', id: cycle.id, title: cycle.name, cycleId: cycle.id }}
              {...btnSecondary(`ml-1 ${HEADER_ACTION_W}`)}
            />
            <FollowButton type="cycle" id={cycle.id} {...btnSecondary(HEADER_ACTION_W)} />

            {/* Cycle focus toggle — module-gated (entry only; see moduleGate). */}
            {(focusModeEnabled || isFocused) && (
            <button
              type="button"
              onClick={() => isFocused ? exitWorkspace() : enterWorkspace(cycleId!, cycle?.name ?? '')}
              title={isFocused ? 'Exit cycle focus mode' : 'Activate cycle focus mode'}
              aria-pressed={isFocused}
              {...(isFocused ? btnPrimary(HEADER_ACTION_W) : btnSecondary(HEADER_ACTION_W))}
            >
              <Crosshair className="h-3.5 w-3.5" />
              {isFocused ? 'In Focus' : 'Focus'}
            </button>
            )}
          </div>
        )}
      </div>

      {workflowError && (
        <div
          className="flex items-center justify-between gap-3 border bg-[var(--coral-dim)] p-3"
          style={{ borderColor: 'var(--coral)' }}
        >
          <p className="text-sm text-[var(--coral)]">{workflowError}</p>
          <button
            type="button"
            onClick={() => setWorkflowError(null)}
            aria-label="Dismiss workflow error"
            className={`shrink-0 px-2 text-[var(--coral)] hover:opacity-70 ${FOCUS_RING}`}
          >
            ×
          </button>
        </div>
      )}

      {workflowSuccess && (
        <div
          className="flex items-center justify-between gap-3 border bg-[var(--green-dim)] p-3"
          style={{ borderColor: 'var(--green)' }}
        >
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">{workflowSuccess}</p>
          <button
            type="button"
            onClick={() => setWorkflowSuccess(null)}
            aria-label="Dismiss workflow confirmation"
            className={`shrink-0 px-2 text-[var(--green)] dark:text-[var(--text-ter)] hover:opacity-70 ${FOCUS_RING}`}
          >
            ×
          </button>
        </div>
      )}

      
      {/* ── Role-contextual view — non-admins see this instead of the full tab shell ── */}
      {!canSeeFullView && cycle && (
        cycleRoleContext.activeSurface === 'strategic' ? (
          <CycleStrategicView
            cycle={cycle}
            loops={cycleId ? getLoopsForCycle(cycleId) : []}
            tracks={cycleTracks}
            sessions={cycleSessions}
          />
        ) : (
          <CycleMyWorkPanel
            context={cycleRoleContext}
            cycle={cycle}
            activeLoop={activeLoop ?? null}
            sessions={cycleSessions}
          />
        )
      )}

      {/* V-01 navigation contract: mirror of the Designer's "Detail view" toggle.
          Shown when a design-stage cycle is opened in detail view via ?advanced=1. */}
      {searchParams.get('advanced') === '1' && designerEnabled && cycle && !isCycleDesignerComplete(cycle) && (
        <div
          className="flex items-center gap-3 border bg-[var(--mustard-dim)] px-4 py-2"
          style={{ borderColor: 'var(--mustard)' }}
        >
          <span className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--mustard)]">
            Design — not launched
          </span>
          <span className="text-sm text-[var(--text-sec)]">
            You're in the detail view of a cycle that is still being designed.
          </span>
          <Link
            to={`/prioritization/cycles/${cycle.id}/designer`}
            className={`ml-auto whitespace-nowrap font-mono text-[0.72rem] uppercase tracking-[0.1em] text-[var(--mustard)] hover:opacity-80 ${FOCUS_RING}`}
          >
            Back to Designer →
          </Link>
        </div>
      )}

      {/* W-2: a designed cycle whose first request hasn't been sent is still
          revisable — give configurators an explicit door back into the Designer
          instead of closing it silently at wizard completion. Once ANY loop
          exists the exchange has started: no more "hasn't been kicked off"
          banner, no more reopen door (owner report 2026-07-09: recipients saw
          this after the request was already in flight). */}
      {designerEnabled && canSeeFullView && cycle && cycle.status === 'draft' && isCycleDesignerComplete(cycle) && (cycleId ? getLoopsForCycle(cycleId).length === 0 : true) && (
        <div
          className="flex items-center gap-3 border bg-[var(--slate-dim)] px-4 py-2"
          style={{ borderColor: 'var(--slate)' }}
        >
          <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-[var(--text-sec)]">
            Ready — first request pending
          </span>
          <span className="text-xs text-[var(--text-sec)]">
            This cycle is designed but hasn't been kicked off. You can still change its design.
          </span>
          <Link
            to={`/prioritization/cycles/${cycle.id}/designer?reopen=1`}
            className={`ml-auto whitespace-nowrap font-mono text-[0.62rem] uppercase tracking-[0.1em] text-[var(--text-sec)] hover:opacity-80 ${FOCUS_RING}`}
          >
            Reopen design →
          </Link>
        </div>
      )}

      {/* Launch creates one loop per track (owner ruling A5), which closed the
          Reopen door above even when nothing had been sent. While every loop is
          still unsent, the owner or an admin can take the launch back
          (reopen_cycle_design, mig 746); the server refuses once a ball exists
          or a session has run, and says why. */}
      {designerEnabled && canSeeFullView && cycle && cycleId && cycle.status === 'draft' && isCycleDesignerComplete(cycle)
        && (cycleRoleContext.isCycleOwner || cycleRoleContext.isAdmin)
        && getLoopsForCycle(cycleId).length > 0
        && getLoopsForCycle(cycleId).every((l) => l.state === 'drafting') && (
        <div
          className="flex items-center gap-3 border bg-[var(--slate-dim)] px-4 py-2"
          style={{ borderColor: 'var(--slate)' }}
        >
          <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-[var(--text-sec)]">
            Launched — nothing sent yet
          </span>
          <span className="text-xs text-[var(--text-sec)]">
            No request has gone out. Take the launch back to change the design; the tracks stay.
          </span>
          <ReverseActionButton
            label="Take back launch"
            confirmTitle="Take back this launch"
            explain="This removes the unsent loops the launch created so you can reopen the design. Tracks and their setup stay. It can't be done once a request has been sent or a session has run."
            run={(reason) => reopenCycleDesign(cycleId, reason)}
            onDone={() => void fetchCatchballLoops(cycleId)}
            className="ml-auto"
          />
        </div>
      )}

      {canSeeFullView && (<>
      {/* Tab Navigation: Tracks always (entry to track detail); Catchball when workflow has it */}
        <div className="border-b border-[var(--rule)] bg-[var(--bg-raised)]">
        <div className="flex items-center gap-1 overflow-x-auto px-2">
          {([
            { id: 'overview' as const, label: 'Overview', icon: ClipboardList },
            ...(catchballEnabled ? [{ id: 'exchange' as const, label: 'Cascade', icon: RefreshCcw }] : []),
            { id: 'defaults' as const, label: 'Defaults', icon: Settings },
            { id: 'tracks' as const, label: 'Tracks', icon: Layers },
            { id: 'results' as const, label: 'Results', icon: Flag },
            { id: 'allocation' as const, label: 'Allocation', icon: Grid2X2 },
            { id: 'history' as const, label: 'Cycle History', icon: Activity },
            { id: 'discussion' as const, label: 'Discussion', icon: MessageSquare },
          ]).map(tab => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setCurrentTab(tab.id)}
                aria-current={isActive ? 'page' : undefined}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 font-mono text-[0.72rem] uppercase tracking-[0.1em] transition-colors ${FOCUS_RING} ${
                  isActive
                    ? 'bg-[var(--hover-tint)] text-[var(--mustard)]'
                    : 'text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]'
                }`}
                style={{ borderBottomColor: isActive ? 'var(--mustard)' : 'transparent' }}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      
      
      {/* Tab Content (v2.3 - organized by work area, not phase) */}
      {/* Not `.card` — that global class hardcodes  + bg-[var(--bg-raised)]. */}
      <div {...panel('p-6')}>

        {/* ── Tracks Tab ── Primary landing: track cards + create, sessions below ── */}
        {currentTab === 'tracks' && (
          <div className="space-y-6">
            {/* Track cards + Create Track button — front and center */}
            <TrackIndex
              cycleId={cycleId || ''}
              cycleName={cycle.name}
            />

            {/* Sessions — collapsible, below the tracks */}
            <div {...panel('overflow-hidden')}>
              <button
                type="button"
                onClick={() => setSessionsExpanded(v => !v)}
                aria-expanded={sessionsExpanded}
                className={`flex w-full items-center justify-between bg-[var(--surface)] px-4 py-3 text-left transition-colors hover:bg-[var(--hover-tint)] ${FOCUS_RING}`}
              >
                <div className="flex items-center gap-2">
                  <Play className="h-4 w-4 text-[var(--text-dim)]" />
                  <span className={LABEL_MONO}>Sessions in this cycle</span>
                  {(Array.isArray(cycleSessions) ? cycleSessions : []).length > 0 && (
                    <span {...chip('slate')}>
                      {(Array.isArray(cycleSessions) ? cycleSessions : []).length}
                    </span>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 text-[var(--text-dim)] transition-transform ${sessionsExpanded ? 'rotate-180' : ''}`} />
              </button>

              {sessionsExpanded && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[var(--rule)] text-sm">
                    <thead className="border-b border-[var(--rule)] bg-[var(--bg-raised)]">
                      <tr>
                        <th className={`px-4 py-2 text-left ${LABEL_MONO}`}>Session</th>
                        <th className={`px-4 py-2 text-left ${LABEL_MONO}`}>Track</th>
                        <th className={`px-4 py-2 text-left ${LABEL_MONO}`}>Status</th>
                        <th className="px-4 py-2 w-8" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--rule)] bg-[var(--bg-raised)]">
                      {(Array.isArray(cycleSessions) ? cycleSessions : []).length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-6 text-center text-sm text-[var(--text-dim)]">
                            No sessions yet — open a Track and create a session there.
                          </td>
                        </tr>
                      ) : (
                        (Array.isArray(cycleSessions) ? cycleSessions : []).map((session: { id: string; name?: string; trackId?: string; cycleId?: string; state?: string }) => {
                          const trackName = session.trackId
                            ? (tracks.find((t) => t.id === session.trackId)?.name ?? session.trackId)
                            : '—';
                          return (
                            <tr
                              key={session.id}
                              className="group cursor-pointer transition-colors hover:bg-[var(--hover-tint)]"
                              onClick={() => navigate(`/prioritization/sessions/${session.id}`)}
                            >
                              <td className="px-4 py-2.5">
                                <Link
                                  to={`/prioritization/sessions/${session.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`text-[var(--text-pri)] underline decoration-dotted decoration-[var(--rule-bright)] underline-offset-4 transition-colors group-hover:text-[var(--mustard)] group-hover:decoration-[var(--mustard)] ${FOCUS_RING}`}
                                >
                                  {session.name || 'Unnamed session'}
                                </Link>
                              </td>
                              <td className="px-4 py-2.5 text-[var(--text-sec)]">
                                {session.trackId ? (
                                  <Link
                                    to={`/prioritization/tracks/${session.trackId}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`text-[var(--text-sec)] underline decoration-dotted decoration-[var(--rule-bright)] underline-offset-4 transition-colors hover:text-[var(--mustard)] hover:decoration-[var(--mustard)] ${FOCUS_RING}`}
                                  >
                                    {trackName}
                                  </Link>
                                ) : (
                                  <span className="text-[var(--text-dim)]">—</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                {/* Status is read, not pressed — readout, not a box. */}
                                <span {...status(
                                  session.state === 'PUBLISHED' ? 'green'
                                    : session.state === 'RUN_COMPLETE' ? 'slate'
                                    : 'neutral',
                                )}>
                                  {session.state || 'DRAFT'}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">
                                <ChevronRight
                                  aria-hidden
                                  className="h-4 w-4 text-[var(--text-ter)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--mustard)]"
                                />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Decision Authority assignment — who ratifies the cycle's priorities */}
            {cycleId && canSeeFullView && (
              <div {...panel('space-y-3 p-4')}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 {...heading('flex items-center gap-2')}>
                      <UserCheck className="h-4 w-4 text-[var(--text-dim)]" />
                      Decision Authority
                    </h4>
                    <p className="mt-1 text-xs text-[var(--text-sec)]">
                      The person who ratifies the priorities when the Catchball cycle concludes.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setDaPickerOpen(v => !v); setDaError(null); }}
                    {...btnSecondary()}
                  >
                    {daPickerOpen ? 'Cancel' : (cycle?.metadata?.decisionMakerId ? 'Change' : 'Assign')}
                  </button>
                </div>
                {cycle?.metadata?.decisionMakerId ? (
                  <div className="flex items-center gap-2">
                    <span {...chip('slate')}>
                      <UserCheck className="h-3.5 w-3.5" />
                      {cycle.metadata.decisionMakerName as string ?? cycle.metadata.decisionMakerId as string}
                    </span>
                  </div>
                ) : (
                  <p className="text-xs italic text-[var(--text-dim)]">No decision authority assigned yet.</p>
                )}
                {daPickerOpen && (
                  <div className="pt-2 space-y-2">
                    <label htmlFor="cycle-decision-authority-select" className="sr-only">Decision authority</label>
                    <Select
                      id="cycle-decision-authority-select"
                      {...field()}
                      defaultValue=""
                      onChange={async (e) => {
                        const selectedId = e.target.value;
                        if (!selectedId || !cycleId) return;
                        const selectedUser = systemUsers.find(u => u.id === selectedId);
                        if (!selectedUser) return;
                        setDaSaving(true);
                        setDaError(null);
                        try {
                          const result = await updateCycle(cycleId, {
                            metadata: {
                              ...(cycle?.metadata ?? {}),
                              decisionMakerId: selectedId,
                              decisionMakerName: selectedUser.name ?? selectedUser.email,
                            },
                          });
                          if (!result.success) setDaError(result.error ?? 'Failed to save');
                          else setDaPickerOpen(false);
                        } catch {
                          setDaError('Failed to save decision authority');
                        } finally {
                          setDaSaving(false);
                        }
                      }}
                    >
                      <option value="" disabled>Select a workspace member…</option>
                      {systemUsers.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.name ?? u.email}
                        </option>
                      ))}
                    </Select>
                    {daSaving && <p className="flex items-center gap-1 text-xs text-[var(--text-dim)]"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</p>}
                    {daError && <p className="text-xs text-[var(--coral)]">{daError}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Decision rights — who decides what, how, and who may reassign it.
                Cycle-scoped; the module gate keeps it out of workspaces that
                have not turned decision rights on. */}
            {cycleId && canSeeFullView && decisionRightsEnabled && (
              <DecisionRightsPanel
                cycleId={cycleId}
                cycleOwnerId={cycle?.ownerId}
                cycleOwnerName={cycleOwnerName}
                canEdit={cycleRoleContext.isCycleOwner || cycleRoleContext.isAdmin}
              />
            )}

            {/* Governance overrides — collapsible, secondary detail */}
            {cycleId && (
              <div {...panel('overflow-hidden')}>
                <button
                  type="button"
                  onClick={() => setConfigExpanded(v => !v)}
                  aria-expanded={configExpanded}
                  className={`flex w-full items-center justify-between bg-[var(--surface)] px-4 py-3 text-left transition-colors hover:bg-[var(--hover-tint)] ${FOCUS_RING}`}
                >
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-[var(--text-dim)]" />
                    <span className={LABEL_MONO}>Governance Overrides</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-[var(--text-dim)] transition-transform ${configExpanded ? 'rotate-180' : ''}`} />
                </button>
                {configExpanded && (
                  <div className="p-4">
                    <CycleGovernanceOverrideSection cycleId={cycleId} />
                  </div>
                )}
              </div>
            )}

            {/* Lock All Tracks (Value Collections / Brief E) */}
            <div {...panel('space-y-3 p-4')}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 {...heading('flex items-center gap-2')}>
                    <Lock className="h-4 w-4 text-[var(--text-dim)]" />
                    Lock All Track Values
                  </h4>
                  <p className="mt-1 text-xs text-[var(--text-sec)]">
                    Freezes the value set for every unlocked Track in this cycle simultaneously.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={lockAllTracksbusy}
                  onClick={() => setShowLockAllConfirm(true)}
                  {...btnPrimary()}
                >
                  {lockAllTracksbusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4" />
                  )}
                  Lock All Tracks
                </button>
              </div>
            </div>

            {/* Confirmation dialog for Lock All Tracks */}
            {showLockAllConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="lock-all-tracks-title"
                  className="mx-4 w-full max-w-md border bg-[var(--bg-raised)] p-6 shadow-xl"
                  style={{ borderColor: 'var(--rule-bright)' }}
                >
                  <h3 id="lock-all-tracks-title" {...heading('mb-2')}>Lock All Track Values?</h3>
                  <p className="mb-6 text-sm leading-relaxed text-[var(--text-sec)]">
                    This will freeze the value set for every unlocked Track in this cycle. Sessions in
                    these tracks will use these values and they cannot be changed without a governance
                    override. Confirm?
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowLockAllConfirm(false)}
                      {...btnSecondary()}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleLockAllTracks}
                      {...btnPrimary()}
                    >
                      Lock All Tracks
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Overview Tab ── Cycle summary: stats, description, collapsed config ── */}
        {currentTab === 'overview' && (
          <div className="space-y-6 min-w-0">
            {/* ── Setup Checklist — draft cycles until track / Catchball prep / first handoff is done ── */}
            {cycle.status === 'draft' && !cycleReport && (() => {
              const hasWorkingTierNodes = workingTierNodes.length > 0;
              const membersReady = computedWorkingTierHasMembers || workingTierHasMembers;

              const readyForFirstRequest =
                !catchballEnabled || !hasWorkingTierNodes || membersReady;
              const firstRequestSent =
                activeLoop != null && activeLoop.state !== 'drafting';

              const showWorkingTierStep = catchballEnabled && hasWorkingTierNodes;
              const stepMetas = [
                ...(showWorkingTierStep ? [{ id: 'members' as const, done: membersReady }] : []),
                ...(catchballEnabled ? [{ id: 'request' as const, done: firstRequestSent }] : []),
              ];
              if (stepMetas.every((s) => s.done)) return null;

              const totalSteps = stepMetas.length;
              const completedCount = stepMetas.filter((s) => s.done).length;

              const requestStepDisplayIndex = showWorkingTierStep ? 2 : 1;

              return (
                <div {...panel('p-5')}>
                  <div className="mb-4 flex items-center gap-3">
                    <div
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center border bg-[var(--surface)]"
                      style={{ borderColor: 'var(--rule-bright)' }}
                    >
                      <ListChecks className="h-4 w-4 text-[var(--mustard)]" />
                    </div>
                    <div className="flex-1">
                      <h3 {...heading()}>Start your first loop</h3>
                      <p className="mt-1 text-xs text-[var(--text-sec)]">Identify who gets the ball first, then hand it off.</p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {!firstRequestSent && (
                        <button
                          type="button"
                          onClick={() => void handleChecklistSendFirstRequest()}
                          disabled={!readyForFirstRequest || checklistSendRequestBusy}
                          {...btnPrimary()}
                        >
                          {checklistSendRequestBusy ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Starting…
                            </>
                          ) : (
                            <>
                              Start loop →
                            </>
                          )}
                        </button>
                      )}
                      <span {...chip('neutral')}>
                        {completedCount} / {totalSteps}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {showWorkingTierStep && (
                      <div>
                        <div
                          className={`flex items-center gap-3 border p-3 transition-colors ${membersReady ? 'bg-[var(--green-dim)]' : 'bg-[var(--surface)]'}`}
                          style={{ borderColor: membersReady ? 'var(--green)' : 'var(--rule)' }}
                        >
                          <div
                            className={`flex h-6 w-6 flex-shrink-0 items-center justify-center border font-mono text-[0.65rem] font-bold ${membersReady ? 'bg-[var(--green)] text-white' : 'bg-[var(--bg-raised)] text-[var(--mustard)]'}`}
                            style={{ borderColor: membersReady ? 'var(--green)' : 'var(--rule-bright)' }}
                          >
                            {membersReady ? '✓' : 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm ${membersReady ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                              Who receives your request?
                            </p>
                            <p className="text-xs text-[var(--text-ter)]">
                              {membersReady
                                ? 'The next level down is set up and ready to receive the ball.'
                                : "Add the people or teams at the next level down. They'll receive your request and organize responses — and they can add the level below them too."}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowWorkingTierPanel((v) => !v)}
                            aria-expanded={showWorkingTierPanel}
                            {...btnSecondary('shrink-0')}
                          >
                            {showWorkingTierPanel ? 'Hide ↑' : 'Set up →'}
                          </button>
                        </div>
                        {showWorkingTierPanel && cycle.orgScopeNodeId && (
                          <div className="mt-2 pl-9">
                            <WorkingTierSetupPanel
                              orgScopeNodeId={cycle.orgScopeNodeId}
                              orgUnitsMap={orgUnitsMap}
                              systemUsers={systemUsers}
                              groups={groups}
                              onMembersChanged={() => setWorkingTierHasMembers(true)}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {catchballEnabled && (
                      <div
                        className={`flex items-start gap-3 border p-3 transition-colors ${firstRequestSent ? 'bg-[var(--green-dim)]' : 'bg-[var(--surface)]'}`}
                        style={{ borderColor: firstRequestSent ? 'var(--green)' : 'var(--rule)' }}
                      >
                        <div
                          className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border font-mono text-[0.65rem] font-bold ${firstRequestSent ? 'bg-[var(--green)] text-white' : 'bg-[var(--bg-raised)] text-[var(--mustard)]'}`}
                          style={{ borderColor: firstRequestSent ? 'var(--green)' : 'var(--rule-bright)' }}
                        >
                          {firstRequestSent ? '✓' : requestStepDisplayIndex}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="min-w-0">
                              <p className={`text-sm ${firstRequestSent ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                                Send your first request
                              </p>
                              <p className="text-xs text-[var(--text-ter)]">
                                {firstRequestSent
                                  ? 'Request sent — the ball is in play'
                                  : activeLoop
                                    ? 'Finish composing and hand off your Catchball request.'
                                    : 'Opens the composer with a new draft loop so you can invite the working tier.'}
                              </p>
                            </div>
                            <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
                              {firstRequestSent ? (
                                <span {...chip('green')}>
                                  Done ✓
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => void handleChecklistSendFirstRequest()}
                                  disabled={!readyForFirstRequest || checklistSendRequestBusy}
                                  {...btnSecondary()}
                                >
                                  {checklistSendRequestBusy ? (
                                    <>
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Starting…
                                    </>
                                  ) : (
                                    'Send Request →'
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {catchballError && currentTab === 'overview' && (
              <div
                className="flex items-start justify-between gap-3 border bg-[var(--mustard-dim)] px-4 py-3 text-sm text-[var(--text-pri)]"
                style={{ borderColor: 'var(--mustard)' }}
              >
                <span>{catchballError}</span>
                <button
                  type="button"
                  onClick={() => setCatchballError(null)}
                  aria-label="Dismiss Catchball error"
                  className={`shrink-0 px-2 text-[var(--mustard)] hover:opacity-70 ${FOCUS_RING}`}
                >
                  ×
                </button>
              </div>
            )}

            {/* Key stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {([
                {
                  icon: Layers,
                  label: 'Tracks',
                  value: cycleTracks.length,
                  color: 'text-[var(--text-pri)]',
                  onClick: () => cycleTracks.length === 1
                    ? navigate(`/prioritization/tracks/${cycleTracks[0].id}`)
                    : setCurrentTab('tracks'),
                },
                {
                  icon: Play,
                  label: 'Sessions',
                  value: (Array.isArray(cycleSessions) ? cycleSessions : []).length,
                  color: 'text-[var(--text-pri)]',
                  onClick: () => navigate(`/prioritization/sessions?cycleId=${cycleId}`),
                },
                {
                  icon: Users,
                  label: 'Participants',
                  value: cycleProgress?.activeParticipantCount ?? 0,
                  color: 'text-[var(--text-pri)]',
                  onClick: () => setCurrentTab('tracks'),
                },
                {
                  icon: Package,
                  label: 'Pool items',
                  value: cycleProgress?.poolItemCount ?? 0,
                  color: 'text-[var(--text-pri)]',
                  onClick: () => navigate(`/prioritization/sessions?cycleId=${cycleId}`),
                },
              ] as const).map(({ icon: Icon, label, value, color, onClick }) => (
                <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  className={`group flex w-full cursor-pointer items-center gap-3 border bg-[var(--surface)] p-4 text-left transition-colors hover:bg-[var(--hover-tint)] ${FOCUS_RING}`}
                  style={{ borderColor: 'var(--rule)' }}
                >
                  {/* The icon gives way on a phone, so the label fits its half-width card */}
                  <div
                    className="hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center border bg-[var(--bg-raised)]"
                    style={{ borderColor: 'var(--rule-bright)' }}
                  >
                    <Icon className={`h-4 w-4 ${color}`} />
                  </div>
                  <div className="min-w-0">
                    <div className={`font-mono text-xl ${color}`}>{value}</div>
                    <div className={LABEL_MONO}>{label}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Cycle report card — shown prominently after ratification */}
            {cycleReport && (
              <Link
                to={`/prioritization/cycles/${cycleId}/report`}
                className={`group flex items-center justify-between border bg-[var(--surface)] p-4 transition-colors hover:bg-[var(--hover-tint)] ${FOCUS_RING}`}
                style={{ borderColor: 'var(--rule-bright)' }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center border bg-[var(--bg-raised)]"
                    style={{ borderColor: 'var(--rule-bright)' }}
                  >
                    <FileText className="h-4 w-4 text-[var(--mustard)]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={LABEL_MONO}>Cycle report</span>
                      <span {...chip(cycleReport.legitimacyState === 'published' ? 'green' : 'slate')}>
                        {cycleReport.legitimacyState === 'published' ? 'Published' : 'Ratified'}
                      </span>
                      <span className="font-mono text-xs text-[var(--text-dim)]">v{cycleReport.version}</span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--text-sec)]">
                      {cycleReport.planOfRecord?.total_items_considered ?? 0} priorities ratified
                      {cycleReport.ratifiedAt && ` · ${new Date(cycleReport.ratifiedAt).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <span className="text-[var(--text-ter)] transition-transform group-hover:translate-x-0.5">
                  <ChevronRight className="h-5 w-5" />
                </span>
              </Link>
            )}

            {/* Readiness row (if cycle has tracks) */}
            {cycleProgress && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border border-[var(--rule)] bg-[var(--surface)] p-4">
                <span className={`flex-shrink-0 ${LABEL_MONO}`}>Readiness</span>
                <span {...status(cycleProgress.activeParticipantCount > 0 ? 'green' : 'neutral')}>
                  {cycleProgress.activeParticipantCount > 0 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                  Participants
                </span>
                <span {...status(cycleProgress.confirmedItemCount > 0 ? 'green' : 'neutral')}>
                  {cycleProgress.confirmedItemCount > 0 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                  Items in pool
                </span>
                <span {...status(cycleProgress.criteriaLocked ? 'green' : 'mustard')}>
                  {cycleProgress.criteriaLocked ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                  Criteria {cycleProgress.criteriaLocked ? 'locked' : 'open'}
                </span>
                <span {...status(cycleProgress.canEnterPrioritize ? 'green' : 'neutral', 'ml-auto')}>
                  {cycleProgress.canEnterPrioritize ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                  {cycleProgress.canEnterPrioritize ? 'Ready to rank' : 'Ranking blocked'}
                </span>
              </div>
            )}

            {/* Description (if set) */}
            {cycle.description && (
              <p
                className="border-l-2 pl-4 text-sm text-[var(--text-sec)]"
                style={{ borderColor: 'var(--rule-bright)' }}
              >
                {cycle.description}
              </p>
            )}

            {/* Priority Formation context — shown when cycle is active and no cycle report yet */}
            {!cycleReport && cycle.status === 'active' && (
              <div className="flex items-start gap-3 border border-[var(--rule)] bg-[var(--surface)] px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className={`mb-1 ${LABEL_MONO}`}>Priority Formation in progress</p>
                  <p className="text-sm text-[var(--text-sec)]">
                    This cycle is forming priorities — comparing candidates, gathering input, and moving toward governed commitment. Ranking results are advisory until Catchball review produces accepted priorities.
                  </p>
                </div>
              </div>
            )}

            {/* Condition resolver — named blockers with remediation links */}
            <ContextualReadinessPanel
              nodeType="cycle"
              nodeId={cycleId}
              collapseWhenReady
            />

            {/* Decision Unit — accumulated state across sessions, loops, and iterations */}
            {cycleContextId && (
              <DecisionUnitPanel
                contextId={cycleContextId}
                activeCycleId={cycleId}
              />
            )}

            {/* What people were asked to do here (1054). */}
            <AssignmentsSection home={{ type: 'cycle', id: cycle.id }} linkedItems={[]} />

          </div>
        )}

        {/* Defaults Tab: Workflow preset + cycle session defaults + optional track defaults */}
        {currentTab === 'defaults' && (
          <div className="space-y-6">
            <div {...panel('p-5')}>
              <div className="mb-3">
                <h3 {...heading()}>Workflow</h3>
                <p className="mt-1 text-xs text-[var(--text-sec)]">
                  The workflow this cycle runs. Changing it affects which stages and
                  surfaces appear for everyone working in the cycle.
                </p>
              </div>
              <span className="flex items-center gap-2">
                {canEditCycleWorkflow ? (
              <>
                <label htmlFor="cycle-detail-workflow-select" className="sr-only">Workflow</label>
                <div className="relative inline-flex items-center">
                  <Select
                    id="cycle-detail-workflow-select"
                    ref={workflowSelectRef}
                    value={cycle.workflowId ?? ''}
                    onChange={(e) => handleWorkflowChange(e.target.value)}
                    className="font-mono text-[0.75rem] uppercase tracking-[0.08em]"
                  >
                    <option value="">{workflowDefaultOptionLabel}</option>
                    {workflows.filter((w) => w.status !== 'archived').map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </Select>
                </div>
              </>
            ) : (
              <span className="font-mono text-[0.7rem] uppercase tracking-[0.08em] text-[var(--text-pri)]">{workflowDisplayName}</span>
            )}
              </span>
            </div>
            <WorkflowPresetSummary cycle={cycle} />
            {/* Cycle phase — restores a way to move the cycle's DEGAP phase
                without putting DEGAP on the cycle's main page (canon: "DEGAP
                in the App (Positioning Rule)"). Visible only to people who
                could actually move it — mirrors transition_cycle_phase's own
                authority (cycle owner / workspace admin). */}
            {cycleId && cyclePhaseEnabled && (cycleRoleContext.isCycleOwner || cycleRoleContext.isAdmin) && (
              <CyclePhaseControl cycleId={cycleId} />
            )}
            <CycleSessionDefaultsSection cycle={cycle} />
            {/* Track owner — optional, per-track default. Set here or directly on each Track. */}
            {defaultTrackForCycle && (
              <div {...panel('p-5')}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 {...heading()}>Track owner</h3>
                    <p className="mt-1 text-xs text-[var(--text-sec)]">
                      Optional. The person accountable for configuring participants and running sessions in this Track. Can be set or changed at any time.
                    </p>
                  </div>
                  <span {...chip('neutral', 'shrink-0')}>
                    Optional
                  </span>
                </div>
                <TrackOwnerSubControl
                  trackId={defaultTrackForCycle.id}
                  workspaceId={effectiveWorkspaceId}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Results Tab: Cycle Report — Brief G */}
        {currentTab === 'results' && (
          <div className="space-y-4">
            {cycleReport && (
              <div className="flex items-center justify-between">
                <h3 {...heading()}>Cycle report</h3>
                <Link
                  to={`/prioritization/cycles/${cycleId}/report`}
                  {...btnSecondary()}
                >
                  Open full page
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
            <CycleReportView
              cycleId={cycleId}
              onReportLoaded={(r) => setCycleReport(r)}
            />
          </div>
        )}
        
        {/* ── Allocation Tab ── shares of committed work by comparison cell (mig 816) ── */}
        {currentTab === 'allocation' && cycleId && (
          <CellAllocationPanel
            cycleId={cycleId}
            canEdit={cycleRoleContext.isCycleOwner || cycleRoleContext.isAdmin}
          />
        )}

        {/* Catchball Tab: only when workflow has Catchball enabled */}
        {/* Catchball tab removed — the Cascade is its own primary surface at /prioritization/cascade/dashboard/:cycleId */}

        {/* ── Cycle History Tab (formerly Provenance) ── */}
        {currentTab === 'history' && cycleId && (
          <CycleProvenanceTab cycleId={cycleId} />
        )}

        {/* ── Discussion Tab ── */}
        {currentTab === 'discussion' && cycleId && (
          <CycleDiscussionTab cycleId={cycleId} workspaceId={cycle.workspaceId} />
        )}

        {/* ── the Cascade Tab — balls list only; Desk opens on its own route (DEP-025) ── */}
        {currentTab === 'exchange' && cycleId && catchballEnabled && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--rule)] px-6 pt-4 pb-3">
              <div>
                <h2 {...heading()}>
                  Balls in this cycle
                </h2>
                <p className="mt-1 text-xs text-[var(--text-sec)]">
                  Open a ball to Pass, Fork, run sessions, or Send — one Desk, no maze.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/prioritization/cascade/inbox/${cycleId}`}
                  {...btnSecondary()}
                >
                  <Inbox className="h-3.5 w-3.5" />
                  Inbox
                </Link>
                {activeLoop && isLoopPastFirstSend(activeLoop.state) ? (
                  <Link to={buildCatchballInboxUrl(cycleId, activeLoop.id)} {...btnPrimary()}>
                    <Send className="h-3.5 w-3.5" />
                    Open Catchball
                  </Link>
                ) : (
                  // The first toss opens over this page, in the toss drawer.
                  <button
                    type="button"
                    onClick={() => useTossDrawerStore.getState().openToss(cycleId, activeLoop?.trackId ?? undefined)}
                    {...btnPrimary()}
                  >
                    <Send className="h-3.5 w-3.5" />
                    {CATCHBALL_LABELS.sendTheBall}
                  </button>
                )}
              </div>
            </div>
            <div className="px-6 py-5">
              <Suspense fallback={<div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[var(--text-dim)]" /></div>}>
                {cycleRoleContext.roles.includes('decision_authority') && activeLoop && (
                  <div className="mb-6">
                    <DecisionAuthorityView
                      cycleId={cycleId}
                      loop={activeLoop}
                      cycleOwnerName={undefined}
                    />
                    <div className="my-6 border-t border-[var(--rule)]" />
                  </div>
                )}
                <CatchballDashboard
                  embedded
                  cycleIdProp={cycleId}
                  onBallSelect={(ballId, loopId) => {
                    navigate(buildCatchballInboxUrl(cycleId, loopId, { ballId }));
                  }}
                  onOpenWorkspace={(loopId) => {
                    navigate(buildCatchballInboxUrl(cycleId, loopId));
                  }}
                />
              </Suspense>
            </div>
          </div>
        )}
      </div>
      </>)}

      {/* Import Outcome Drivers Modal */}
      {showImportModal && savedDrivers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowImportModal(false)} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-drivers-title"
            className="relative w-full max-w-md border bg-[var(--bg-raised)] p-6 shadow-xl"
            style={{ borderColor: 'var(--rule-bright)' }}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 id="import-drivers-title" {...heading()}>
                Import Outcome Drivers
              </h2>
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                aria-label="Close import dialog"
                className={`p-1 text-[var(--text-dim)] hover:text-[var(--text-pri)] ${FOCUS_RING}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-sm text-[var(--text-sec)]">
                From project: <span className="text-[var(--text-pri)]">{savedDrivers.projectName}</span>
              </p>
              <div className="max-h-60 space-y-2 overflow-y-auto">
                {savedDrivers.drivers.map((driver, i) => (
                  <div key={i} className="border border-[var(--rule)] bg-[var(--surface)] p-3">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-[var(--mustard)]" />
                      <span className="text-sm text-[var(--text-pri)]">{driver.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                {...btnSecondary('flex-1')}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  // Import all drivers
                  savedDrivers.drivers.forEach(driver => {
                    addOutcomeDriver(cycle!.id, driver.title);
                  });
                  // Clear cycle-specific saved drivers after import
                  localStorage.removeItem(`peak-outcome-drivers-${cycle!.id}`);
                  setSavedDrivers(null);
                  setShowImportModal(false);
                }}
                {...btnPrimary('flex-1')}
              >
                Import All ({savedDrivers.drivers.length})
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* OST Solution Picker Modal */}
      {showOSTPicker && cycle && (
        <OSTSolutionPicker
          cycleId={cycle.id}
          workspaceId={cycle.workspaceId}
          userId={userId}
          onSolutionAdded={(candidateId, solution) => {
            console.log('[CycleDetail] Solution imported:', solution.title, '→', candidateId);
            refreshProgressAndReadiness();
          }}
          onClose={() => setShowOSTPicker(false)}
        />
      )}
      
      {/* Build Proposal from OST Modal */}
      {showProposalBuilder && cycle && activeLoop && (
        <BuildProposalFromOST
          cycleId={cycle.id}
          loopId={activeLoop.id}
          onClose={() => setShowProposalBuilder(false)}
          onSubmitted={(proposalId) => {
            console.log('[CycleDetail] Proposal submitted:', proposalId);
            setShowProposalBuilder(false);
          }}
        />
      )}
      
      {/* RICE Scoring Panel */}
      <SlideOverPanelStandalone
        open={showRicePanel}
        onClose={() => setShowRicePanel(false)}
        title="RICE Scoring"
        size="xl"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowRicePanel(false)}
              {...btnSecondary()}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                // Save scores - TODO: integrate with store
                setShowRicePanel(false);
              }}
              {...btnPrimary()}
            >
              Save Scores
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          <div
            className="border bg-[var(--slate-dim)] p-4"
            style={{ borderColor: 'var(--slate)' }}
          >
            <h4 {...heading('mb-2')}>About RICE Scoring</h4>
            <p className="text-sm text-[var(--text-sec)]">
              RICE stands for Reach, Impact, Confidence, and Effort. Score each item to calculate priority.
            </p>
            <p className="mt-2 text-sm text-[var(--text-sec)]">
              <strong className="text-[var(--text-pri)]">RICE Score</strong> = (Reach × Impact × Confidence) ÷ Effort
            </p>
          </div>

          {/* Items to score */}
          <div className="space-y-4">
            <h4 {...heading()}>Items for Ranking ({cycle?.itemIds?.length || 0})</h4>
            {(cycle?.itemIds || []).map((itemId, index) => {
              const item = items.find(c => c.id === itemId);
              if (!item) return null;
              
              return (
                <div key={itemId} {...panel('p-4')}>
                  <div className="mb-4 flex items-start justify-between">
                    <div>
                      <span className="font-mono text-xs text-[var(--text-dim)]">#{index + 1}</span>
                      <h5 className="text-[var(--text-pri)]">{item.title}</h5>
                      {item.description && (
                        <p className="mt-1 text-sm text-[var(--text-sec)]">{item.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label htmlFor={`rice-reach-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>
                        Reach
                      </label>
                      <input
                        id={`rice-reach-${itemId}`}
                        type="number"
                        placeholder="Users/period"
                        {...field()}
                      />
                    </div>
                    <div>
                      <label htmlFor={`rice-impact-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>
                        Impact
                      </label>
                      <Select id={`rice-impact-${itemId}`} {...field()}>
                        <option value="">Select...</option>
                        <option value="3">Massive (3x)</option>
                        <option value="2">High (2x)</option>
                        <option value="1">Medium (1x)</option>
                        <option value="0.5">Low (0.5x)</option>
                        <option value="0.25">Minimal (0.25x)</option>
                      </Select>
                    </div>
                    <div>
                      <label htmlFor={`rice-confidence-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>
                        Confidence
                      </label>
                      <Select id={`rice-confidence-${itemId}`} {...field()}>
                        <option value="">Select...</option>
                        <option value="1">High (100%)</option>
                        <option value="0.8">Medium (80%)</option>
                        <option value="0.5">Low (50%)</option>
                      </Select>
                    </div>
                    <div>
                      <label htmlFor={`rice-effort-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>
                        Effort
                      </label>
                      <input
                        id={`rice-effort-${itemId}`}
                        type="number"
                        placeholder="Person-months"
                        {...field()}
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between border-t border-[var(--rule)] pt-3">
                    <span className={LABEL_MONO}>RICE Score:</span>
                    <span className="font-mono text-lg text-[var(--mustard)]">—</span>
                  </div>
                </div>
              );
            })}
            
            {(!cycle?.itemIds || cycle.itemIds.length === 0) && (
              <div className="border border-[var(--rule)] bg-[var(--surface)] py-8 text-center">
                <p className="text-sm text-[var(--text-sec)]">No items configured for ranking in this cycle.</p>
                <p className="mt-1 text-sm text-[var(--text-dim)]">Add items to a track before running ranking work.</p>
              </div>
            )}
          </div>
        </div>
      </SlideOverPanelStandalone>
      
      {/* WSJF Scoring Panel */}
      <SlideOverPanelStandalone
        open={showWsjfPanel}
        onClose={() => setShowWsjfPanel(false)}
        title="WSJF Scoring"
        size="xl"
        footer={
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowWsjfPanel(false)} {...btnSecondary()}>Cancel</button>
            <button type="button" onClick={() => setShowWsjfPanel(false)} {...btnPrimary()}>Save Scores</button>
          </div>
        }
      >
        <div className="space-y-6">
          <div
            className="border bg-[var(--green-dim)] p-4"
            style={{ borderColor: 'var(--green)' }}
          >
            <h4 {...heading('mb-2')}>About WSJF (Weighted Shortest Job First)</h4>
            <p className="text-sm text-[var(--text-sec)]">
              WSJF prioritizes by dividing Cost of Delay by job size. Higher scores indicate higher priority.
            </p>
            <p className="mt-2 text-sm text-[var(--text-sec)]">
              <strong className="text-[var(--text-pri)]">WSJF</strong> = (Business Value + Time Criticality + Risk Reduction) ÷ Job Size
            </p>
          </div>

          <div className="space-y-4">
            <h4 {...heading()}>Items ({cycle?.itemIds?.length || 0})</h4>
            {(cycle?.itemIds || []).slice(0, 5).map((itemId) => {
              const item = items.find(c => c.id === itemId);
              if (!item) return null;
              
              return (
                <div key={itemId} {...panel('p-4')}>
                  <h5 className="mb-3 text-[var(--text-pri)]">{item.title}</h5>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label htmlFor={`wsjf-value-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>Business Value</label>
                      <Select id={`wsjf-value-${itemId}`} {...field()}>
                        <option value="">1-10</option>
                        {[1,2,3,4,5,6,7,8,9,10].map(v => <option key={v} value={v}>{v}</option>)}
                      </Select>
                    </div>
                    <div>
                      <label htmlFor={`wsjf-time-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>Time Criticality</label>
                      <Select id={`wsjf-time-${itemId}`} {...field()}>
                        <option value="">1-10</option>
                        {[1,2,3,4,5,6,7,8,9,10].map(v => <option key={v} value={v}>{v}</option>)}
                      </Select>
                    </div>
                    <div>
                      <label htmlFor={`wsjf-risk-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>Risk Reduction</label>
                      <Select id={`wsjf-risk-${itemId}`} {...field()}>
                        <option value="">1-10</option>
                        {[1,2,3,4,5,6,7,8,9,10].map(v => <option key={v} value={v}>{v}</option>)}
                      </Select>
                    </div>
                    <div>
                      <label htmlFor={`wsjf-size-${itemId}`} className={`mb-1 block ${LABEL_MONO}`}>Job Size</label>
                      <Select id={`wsjf-size-${itemId}`} {...field()}>
                        <option value="">1-10</option>
                        {[1,2,3,4,5,6,7,8,9,10].map(v => <option key={v} value={v}>{v}</option>)}
                      </Select>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-[var(--rule)] pt-3">
                    <span className={LABEL_MONO}>WSJF Score:</span>
                    <span className="font-mono text-lg text-[var(--green)] dark:text-[var(--text-ter)]">—</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SlideOverPanelStandalone>
      
      {/* Pairwise Ranking Modal */}
      <ModalPanelStandalone
        open={showPairwiseModal}
        onClose={() => setShowPairwiseModal(false)}
        title="Pairwise Ranking"
        size="lg"
      >
        <div className="space-y-6">
          <div
            className="border bg-[var(--surface)] p-4"
            style={{ borderColor: 'var(--rule-bright)' }}
          >
            <h4 {...heading('mb-2')}>About Pairwise Ranking</h4>
            <p className="text-sm text-[var(--text-sec)]">
              Compare items two at a time to determine relative priority. This method is intuitive and reduces cognitive load.
            </p>
          </div>

          <div className="py-8 text-center">
            <p className={`mb-6 ${LABEL_MONO}`}>Which is more important?</p>

            <div className="mx-auto grid max-w-2xl grid-cols-2 gap-8">
              <button
                type="button"
                className={`border bg-[var(--bg-raised)] p-6 text-left transition-colors hover:bg-[var(--hover-tint)] ${FOCUS_RING}`}
                style={{ borderColor: 'var(--rule-bright)' }}
              >
                <h5 className="text-[var(--text-pri)]">
                  {items[0]?.title || 'Item A'}
                </h5>
                <p className="mt-2 text-sm text-[var(--text-sec)]">
                  {items[0]?.description?.slice(0, 100) || 'Description...'}
                </p>
              </button>

              <button
                type="button"
                className={`border bg-[var(--bg-raised)] p-6 text-left transition-colors hover:bg-[var(--hover-tint)] ${FOCUS_RING}`}
                style={{ borderColor: 'var(--rule-bright)' }}
              >
                <h5 className="text-[var(--text-pri)]">
                  {items[1]?.title || 'Item B'}
                </h5>
                <p className="mt-2 text-sm text-[var(--text-sec)]">
                  {items[1]?.description?.slice(0, 100) || 'Description...'}
                </p>
              </button>
            </div>

            <button type="button" {...btnSecondary('mt-6')}>
              They're equal
            </button>
          </div>

          <div className="border-t border-[var(--rule)] pt-4">
            <div className="flex items-center justify-between font-mono text-xs text-[var(--text-ter)]">
              <span>Comparison 1 of {Math.max(1, (cycle?.itemIds?.length || 0) * ((cycle?.itemIds?.length || 0) - 1) / 2)}</span>
              <div className="flex items-center gap-4">
                <div className="h-2 w-48 bg-[var(--rule-bright)]">
                  <div className="h-2 w-1/12 bg-[var(--mustard)]" />
                </div>
                <span>8%</span>
              </div>
            </div>
          </div>
        </div>
      </ModalPanelStandalone>

      <CreateTrackModal
        open={checklistCreateTrackOpen}
        onClose={() => setChecklistCreateTrackOpen(false)}
        cycleId={cycle.id}
        cycleName={cycle.name}
        onTrackCreated={(track) => {
          refreshProgressAndReadiness();
          void fetchTracksForCycle(cycle.id);
          // Next step: the new track's Defaults tab, where its item scope is set.
          navigate(`/prioritization/tracks/${track.id}?tab=defaults`);
        }}
      />
    </div>
  );
}
